import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Navbar.scss';
import IMAGES from '../../../assets/img/image';
import { useSelector } from 'react-redux';
import { MODULES, CRM_SUBMODULES, CRM_ADMIN_ROLE, hasCrmSubAccess, CrmSubmodule } from '../../../utils/constants';

const navigationMap: any = {
    BLOCK: "/block",
    SEO: "/seo",
    PAGE_LIST: "/layout",
    MANAGE_CAMPAIGN: "/campaign",
    WHATSAPP_CAMPAIGN: "/whatsappCampaign",
    // AI_POWERED: "/aiPowered",
    COUPON: "/coupon",
    USER_MANAGEMENT: "/userManagement",
    PROFILE: "/profile",
    QUESTION_ANSWER: "/questionAnswer",
    CLICK_STREAM: "/clickStream",
    PRODUCT_TAGS: "/productTagging",
    BLOCK_CUSTOMER: "/blockCustomer",
    SERVICEABILITY: "/serviceability",
    PART_PAYMENT: "/part-payment",
    FBT: "/fbt",
    BLOCKS: "/blocks/brand",
    FAQ_SCHEMA: "/faq",
    REVIEWS_MODERATION: "/review",
    ARTICLE: "/article",
    PLA_FEED: "/plaFeed",
    CACHE_DELETION: "/cacheDelete",
    PAGE_MAPPING: "/pageMapping",
    ATTRIBUTE_LISTING: "/attributeListing",
    MEXPRESS: "/mexpress",
    URL_SHORTENER: "/url-shortener",
    PROMOCODE_LIST: "/promocode-list",
    FILE_UPLOADER: "/file-uploader",
    PRODUCT_PRICING: "/product-pricing",
    ORDER_CHATBOT_ANALYTICS: "/order-chatbot-analytics",
    PAYMENT_MODE_MANAGEMENT: "/payment-mode-management",
    PROMPT_MANAGEMENT: "/prompt-management",
    WHATSAPP_CHATBOT_ANALYTICS: "/whatsapp-chatbot-analytics",
    PRODUCT_CHATBOT_ANALYSIS: "/product-chatbot-analysis",
    OTP_ANALYTICS: "/otp-analytics",
    CRM_DASHBOARD: "/crm/dashboard",
    OCR: "/ocr",
    STOCK_ALERT: "/stock-alert",
    RFQ_CARE: "/crm/rfq-care",
    DEVELOPER_TOOLKIT_TRACE: "/crm/developer-toolkit-trace",
    GENAI_COST_ANALYTICS: "/genai-cost-analytics",
    DYNAMIC_PRICING: "/dynamic-pricing",
    WARRANTY_SERVICE_APP: "/warranty-service-app",
    SHIPPING_PORTAL: "/shipping-portal",
};

// CRM sub-menus in priority order. On clicking the CRM tab we redirect to the
// first one the user actually has access to.
const CRM_MENU: { path: string; permission: CrmSubmodule }[] = [
    { path: '/crm/dashboard', permission: CRM_SUBMODULES.DASHBOARD },
    { path: '/crm/ozonetel-dashboard', permission: CRM_SUBMODULES.OZONETEL_DASHBOARD },
    { path: '/crm/agent-performance', permission: CRM_SUBMODULES.AGENT_PERFORMANCE },
    { path: '/crm/escalations', permission: CRM_SUBMODULES.ESCALATIONS },
    { path: '/crm/ozonetel-call-track', permission: CRM_SUBMODULES.CALL_TRACK },
    { path: '/crm/search', permission: CRM_SUBMODULES.CUSTOMERS },
    { path: '/crm/freshdesk-tickets', permission: CRM_SUBMODULES.FRESHDESK_TICKETS },
    { path: '/crm/interaction-timeline', permission: CRM_SUBMODULES.INTERACTION_TIMELINE },
    { path: '/crm/samaira-rfq-calls', permission: CRM_SUBMODULES.SAMAIRA_RFQ_CALLS },
    { path: '/crm/ai-call-simulator', permission: CRM_SUBMODULES.AI_CALL_SIMULATOR },
    { path: '/crm/developer-toolkit-trace', permission: CRM_SUBMODULES.DEV_TOOLKIT_TRACE },
];

const SideNavbar = () => {
    const { user } = useSelector((s: any) => s.authStore);
    const navigate = useNavigate();

    const hasAccess = (module: string) =>
        user?.roles?.includes("Super Admin") || user?.roles?.includes(module) || process.env.REACT_APP_ENVIRONMENT === "DEVELOPMENT";

    // Show the CRM tab if the user has any CRM_* role (or CRM module / CRM_ADMIN / Super Admin).
    const hasAnyCrmAccess = () => {
        if (process.env.REACT_APP_ENVIRONMENT === "DEVELOPMENT") return true;
        const roles: string[] | undefined = user?.roles;
        if (!roles?.length) return false;
        return roles.some(
            (r) => r === "Super Admin" || r === MODULES.CRM || r === CRM_ADMIN_ROLE || r.startsWith("CRM_")
        );
    };

    const navigationHandler = (type: string) => {
        // CRM tab → redirect to the first CRM sub-menu the user can access.
        if (type === 'CRM_DASHBOARD') {
            const firstAccessible = CRM_MENU.find((m) => hasCrmSubAccess(user?.roles, m.permission));
            navigate(firstAccessible ? firstAccessible.path : navigationMap.CRM_DASHBOARD);
            return;
        }
        const path = navigationMap[type];
        if (path) {
            navigate(path);
        }
    };

    // Define menu items as objects
    const menuItems = [
        { label: 'Adsense Campaign', type: 'MANAGE_CAMPAIGN', module: MODULES.ADSENSE_CAMPAIGN },
        { label: 'Article', type: 'ARTICLE', module: MODULES.ARTICLE },
        { label: 'Attribute Listing', type: 'ATTRIBUTE_LISTING', module: MODULES.ATTRIBUTE_LISTING },
        { label: 'Block Customer', type: 'BLOCK_CUSTOMER', module: MODULES.BLOCK_CUSTOMER },
        { label: 'Blocks', type: 'BLOCKS', module: MODULES.BLOCKS },
        { label: 'Cache Deletion', type: 'CACHE_DELETION', module: MODULES.CACHE_DELETE },
        { label: 'CMS Layout', type: 'PAGE_LIST', module: MODULES.LAYOUT },
        { label: 'Dynamic Pricing', type: 'DYNAMIC_PRICING', module: MODULES.DYNAMIC_PRICING },
        { label: 'CRM', type: 'CRM_DASHBOARD', module: MODULES.CRM },
        { label: 'FAQ', type: 'FAQ_SCHEMA', module: MODULES.FAQ },
        { label: 'FBT', type: 'FBT', module: MODULES.FBT },
        { label: 'File Uploader', type: 'FILE_UPLOADER', module: MODULES.FILE_UPLOADER },
        { label: 'GenAI Cost Analytics', type: 'GENAI_COST_ANALYTICS', module: MODULES.GENAI_COST_ANALYTICS },
        { label: 'Order Chatbot Analytics', type: 'ORDER_CHATBOT_ANALYTICS', module: MODULES.ORDER_CHATBOT_ANALYTICS },
        { label: 'OTP Analytics', type: 'OTP_ANALYTICS', module: MODULES.OTP_ANALYTICS },
        { label: 'Page Mapping', type: 'PAGE_MAPPING', module: MODULES.PAGE_MAPPING },
        { label: 'Part Payment', type: 'PART_PAYMENT', module: MODULES.PART_PAYMENT },
        { label: 'Payment Mode Management', type: 'PAYMENT_MODE_MANAGEMENT', module: MODULES.PAYMENT_MODE_MANAGEMENT },
        { label: 'PLA Feed', type: 'PLA_FEED', module: MODULES.PLA_FEED },
        { label: 'Product Chatbot Analysis', type: 'PRODUCT_CHATBOT_ANALYSIS', module: MODULES.PRODUCT_CHATBOT_ANALYSIS },
        { label: 'Product Pricing', type: 'PRODUCT_PRICING', module: MODULES.PRODUCT_PRICING },
        { label: 'Product Tags', type: 'PRODUCT_TAGS', module: MODULES.PRODUCT_TAGGING },
        { label: 'Profile', type: 'PROFILE', module: null },
        { label: 'Promocode', type: 'PROMOCODE_LIST', module: MODULES.PROMOCODE_LIST },
        { label: 'Prompt Management', type: 'PROMPT_MANAGEMENT', module: MODULES.PROMPT_MANAGEMENT },
        { label: 'Q & A', type: 'QUESTION_ANSWER', module: MODULES.QNA },
        { label: 'Reviews Moderation', type: 'REVIEWS_MODERATION', module: MODULES.REVIEW },
        { label: 'Rfq Care', type: 'RFQ_CARE', module: MODULES.CRM },
        { label: 'Serviceability', type: 'SERVICEABILITY', module: MODULES.SERVICEABILITY },
        { label: 'Shipping Portal', type: 'SHIPPING_PORTAL', module: MODULES.SHIPPING_PORTAL },
        { label: 'Stock Alert', type: 'STOCK_ALERT', module: MODULES.STOCK_ALERT },
        { label: 'URL Shortener', type: 'URL_SHORTENER', module: MODULES.URL_SHORTENER },
        { label: 'User Management', type: 'USER_MANAGEMENT', module: MODULES.USER_MANAGEMENT },
        { label: 'Warranty Service App', type: 'WARRANTY_SERVICE_APP', module: MODULES.WARRANTY_SERVICE_APP },
        { label: 'Whatsapp Campaign', type: 'WHATSAPP_CAMPAIGN', module: MODULES.WHATSAPP_CAMPAIGN },
        { label: 'WhatsApp Chatbot Analytics', type: 'WHATSAPP_CHATBOT_ANALYTICS', module: MODULES.WHATSAPP_CHATBOT_ANALYTICS },
    ];

    // Sort menu items alphabetically by label
    const sortedMenuItems = menuItems.sort((a, b) => a.label.localeCompare(b.label));

    return (
        <div className='leftsidewrap'>
            <div className='bg-[#43425d] h-full'>
                <div className='moglogo'>
                    <img src={IMAGES.LOGO.darklogo} alt="" className='img-responsive' />
                </div>
                <ul className='leftsidemenu'>
                    {sortedMenuItems.map((item) => {
                        const canShow =
                            item.type === 'CRM_DASHBOARD'
                                ? hasAnyCrmAccess()
                                : item.module === null || hasAccess(item.module);
                        if (canShow) {
                            return (
                                <li key={item.label} onClick={() => navigationHandler(item.type)} className='blocksList'>
                                    <a>{item.label}</a>
                                </li>
                            );
                        }
                        return null;
                    })}
                </ul>
            </div>
        </div>
    );
};

export default SideNavbar;